import React, { useState } from 'react';
import './FaqSection.css';

const FAQ_ITEMS = [
  {
    q: 'How is this different from other inspection AI tools?',
    a: [
      'Most AI inspection tools are reactive point solutions used just to spot anomalies on a single image. NDE/NDT failures don\'t happen at only one level, though. They occur in procedures, in execution, in reporting and documentation, in the inspection images themselves, and in how those images are interpreted. A point solution can only see one slice of that.',
      'Orbix is an agentic AI system embedded directly into a digital workflow platform, which is fundamentally different from the standalone tools common in this industry. It provides total project quality control across the full lifecycle. First, it ensures service company procedures are optimized and aligned with code and project-specific requirements, so the plan is correct before work begins. Then it enforces that the work is actually carried out correctly, reasoning across examination reports, inspection data, and documentation as they\'re produced.',
      'This works because Orbix has live access to your entire project state. It can reason across everything at once: certifications, procedures, asset owner specifications, component and equipment information, every user decision, all previous reports and data, every user communication, and the inspection imagery and data itself. Exceptions surface on the spot, and your team can take corrective action before a submission is finalized.',
    ],
  },
  {
    q: 'What does the AI quality team actually review?',
    a: [
      'We utilize SME-crafted compliance matrices to review your foundational procedures, ensuring they are perfectly aligned with the asset owner\'s specific codes and requirements. Orbix then continuously checks the field execution. It automatically reviews every examination report, technique, review decision, and piece of inspection imagery to guarantee that all of the inspection work is being done correctly.',
    ],
  },
  {
    q: 'Does it change how our technicians work?',
    a: [
      'Technicians on projects utilizing Orbix oversight simply drag and drop their examination reports and inspection data into their submission portal at the end of the day. If the system detects any exceptions or critical failures, it sends an email within typically 20 minutes to inform them of what the problem is. This ensures they can take immediate corrective action and fix the issue quickly.',
    ],
  },
  {
    q: 'Can we customize what it monitors?',
    a: [
      'Yes. The oversight is automatically tailored to your project\'s specific requirements, but it is completely customizable. You can easily add, remove, or modify any specific checks you need, and Orbix will enforce those exact standards across all submissions.',
    ],
  },
  {
    q: 'How do we receive the results?',
    a: [
      'Technicians are notified in real time when exceptions are detected, allowing them to take immediate corrective action. Auditors and managers have separate access to dedicated dashboards that provide complete transparency into the project. These dashboards display the real-time compliance health of the project, track every finding from detection to closeout, and keep all documentation and compliance records current.',
    ],
  },
  {
    q: 'Does the system replace our human auditors?',
    a: [
      'No. Orbix is an oversight system designed to support human review, not replace it. It gives auditors and quality control teams greater visibility. By surfacing exceptions automatically, it makes human reviewers much more efficient, allowing them to provide thorough, timely oversight across multiple locations from one centralized platform.',
    ],
  },
  {
    q: 'What happens if a project manager disagrees with a review decision?',
    a: [
      'If an auditor or manager needs to override a system verdict, they can easily do so. Orbix logs the override along with the manager\'s provided justification. The AI system then takes that specific reasoning into consideration when reviewing evidence for the remainder of the project.',
    ],
  },
  {
    q: 'What is PROVE?',
    a: [
      'PROVE is the decision protocol that powers our AI oversight. It ensures the accuracy and reliability of every review decision by subjecting every initial finding to a bidirectional adversarial peer review. This challenges the system to prove its work before a verdict is finalized. Once verified, the decision is frozen into a permanent proof artifact that directly links the outcome to the exact evidence used.',
      'Standard AI agents rely on open-ended reasoning loops that stop checking as soon as the system "feels" satisfied, which often leads to missed requirements or rushed decisions. PROVE replaces these self-guided loops with a controlled pipeline. By forcing the system to defend its decisions against an independent adversarial review, PROVE ensures every final verdict is accurate, grounded in the actual evidence, and effortless for a human auditor to verify.',
    ],
  },
];

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className="pm-web-faq-item" data-open={isOpen ? '1' : '0'}>
      <button className="pm-web-faq-q" type="button" onClick={onToggle}>
        <span>{item.q}</span>
        <i className="bx bx-plus pm-web-faq-icon"></i>
      </button>
      <div
        className="pm-web-faq-a"
        style={{ maxHeight: isOpen ? '2000px' : '0px' }}
      >
        <div className="pm-web-faq-a__inner">
          {item.a.map((para, i) => <p key={i}>{para}</p>)}
        </div>
      </div>
    </div>
  );
}

function FaqSection() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section className="pm-web-sec pm-web-faq">
      <div className="pm-web-faq-glow pm-web-faq-glow--a" aria-hidden="true"></div>
      <div className="pm-web-faq-glow pm-web-faq-glow--b" aria-hidden="true"></div>
      <div className="pm-web-wrap pm-web-wrap--narrow">
        <div className="pm-web-head pm-reveal" style={{ textAlign: 'center' }}>
          <span className="pm-web-eyebrow">Frequently asked questions</span>
          <h2 className="pm-web-h2">FAQ</h2>
        </div>

        <div className="pm-web-faq-list pm-reveal" style={{ '--reveal-delay': '120ms' }}>
          {FAQ_ITEMS.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default FaqSection;
